/**
 * MaturityBadge (D2) — renders a context's `maturity_stage` as a coloured pill
 * plus the human "next step" hint for that stage (upd-1.md §7). The lifecycle is
 * ambient_field → … → infrastructure. Per the design system the badge stays in
 * the black/neutral register (no status chroma): early, unstructured stages read
 * as a quiet `outline`, mid stages as a `secondary` chip, and the resolver-ready
 * stages (executable+) carry the single black `default` accent. This is the "not
 * a bureaucratic form, but a maturity badge" UX the spec calls for.
 */
import * as React from "react";

import type { ContextMaturityStage } from "@aos/shared";
import { CONTEXT_MATURITY_STAGES } from "@aos/shared";

import { Badge, type BadgeProps } from "@/components/ui/badge";
import { humanize } from "@/lib/format";

type BadgeVariant = NonNullable<BadgeProps["variant"]>;

/**
 * Per-stage badge tone — black/neutral register only (design-system §1: black is
 * the only accent). Early stages are a quiet `outline`, mid stages a `secondary`
 * chip, and the resolver-ready stages (executable+) carry the black `default`.
 */
const STAGE_VARIANT: Record<ContextMaturityStage, BadgeVariant> = {
  ambient_field: "outline",
  attention_field: "outline",
  signal_field: "outline",
  named: "secondary",
  bounded: "secondary",
  structured: "secondary",
  operational: "secondary",
  executable: "default",
  governed: "default",
  infrastructure: "default",
};

/**
 * The "next step" hint shown alongside the badge — what the operator should do
 * to mature this context one stage further (upd-1.md §7). The terminal
 * `infrastructure` stage has no further step.
 */
const STAGE_NEXT_STEP: Record<ContextMaturityStage, string> = {
  ambient_field: "Name this area of attention",
  attention_field: "Start collecting signals",
  signal_field: "Define boundary and bind first packages",
  named: "Define scope and boundary",
  bounded: "Bind artifacts, packages and actors",
  structured: "Use it in real work",
  operational: "Make it resolver-loadable (executable)",
  executable: "Add metrics and a review cycle",
  governed: "Add violation monitoring",
  infrastructure: "Stable — maintain and review periodically",
};

/** 1-based ordinal of a stage within the lifecycle (for the progress hint). */
function stageOrdinal(stage: ContextMaturityStage): number {
  const idx = CONTEXT_MATURITY_STAGES.indexOf(stage);
  return idx < 0 ? 0 : idx + 1;
}

/** The next-step hint for a given maturity stage (exported for cards/details). */
export function maturityNextStep(stage: ContextMaturityStage): string {
  return STAGE_NEXT_STEP[stage] ?? "Advance to the next stage";
}

export interface MaturityBadgeProps
  extends Omit<BadgeProps, "variant" | "children"> {
  stage: ContextMaturityStage;
  /** Append " · N/10" lifecycle position after the label. Default false. */
  showOrdinal?: boolean;
}

/** Coloured maturity pill for a context (upd-1.md §7). */
export function MaturityBadge({
  stage,
  showOrdinal = false,
  ...props
}: MaturityBadgeProps) {
  const variant = STAGE_VARIANT[stage] ?? "secondary";
  const label = humanize(stage);
  return (
    <Badge variant={variant} {...props}>
      {label}
      {showOrdinal
        ? ` · ${stageOrdinal(stage)}/${CONTEXT_MATURITY_STAGES.length}`
        : null}
    </Badge>
  );
}

export default MaturityBadge;
